import { newIdempotencyKey } from "../api/idempotency"

/**
 * Книга ключей идемпотентности: НАМЕРЕНИЕ → ключ.
 *
 * ЗАЧЕМ ОНА ВООБЩЕ НУЖНА. Ключ идемпотентности имеет смысл ровно тогда, когда он
 * переживает повтор: администратор нажал «Клиент пришёл», ответ не доехал, он нажал
 * ещё раз — и обязан получить тот же результат, а не вторую отметку. Ключ, созданный
 * в момент отправки, у каждой попытки новый, то есть бесполезный.
 *
 * НО ОДИН КЛЮЧ НА ВСЮ ЖИЗНЬ ЭКРАНА ТОЖЕ НЕЛЬЗЯ. Повтор с тем же ключом и ДРУГИМ телом —
 * это уже не повтор, и сервер вправе ответить отказом. Значит, ключ обязан меняться
 * вместе с намерением: «отметить приход этой записи в этой версии» и «отметить приход
 * той же записи после того, как её перенесли» — разные намерения.
 *
 * ОТСЮДА УСТРОЙСТВО. Намерение описывается строкой, в которую входит всё, от чего зависит
 * тело запроса: действие, запись, версия записи (expectedUpdatedAt), а для создания —
 * содержимое черновика. Одинаковая строка отдаёт один и тот же ключ, разная — разные.
 * После успеха намерение забывается: следующее такое же действие получит новый ключ.
 *
 * Функция чистая и проверяется тестом, потому что сломать её молча очень легко:
 * ошибка здесь не видна на экране ни одним пикселем и всплывает только дублем визита
 * в базе салона.
 */

export interface IdempotencyBook {
  /** Ключ для намерения. Повторный вызов с той же строкой отдаёт ТОТ ЖЕ ключ. */
  readonly keyFor: (intent: string) => string
  /** Намерение исполнено: следующий такой же запрос — уже другое намерение. */
  readonly forget: (intent: string) => void
  /** Сколько намерений в работе. Нужно проверке, чтобы поймать утечку ключей. */
  readonly size: () => number
}

/**
 * ПОЧЕМУ КНИГА НЕ ЧИСТИТСЯ САМА ПО ВРЕМЕНИ. Забывает её вызывающий код — после успеха
 * и после отказа, который отменяет намерение (например, «запись уже изменили»).
 * Тайм-аут здесь означал бы, что ключ протухает во время самого запроса — то есть
 * ровно тогда, когда он нужен.
 */
export function createIdempotencyBook(
  makeKey: (scope: string) => string = newIdempotencyKey,
): IdempotencyBook {
  const keys = new Map<string, string>()

  return {
    keyFor(intent: string): string {
      const known = keys.get(intent)
      if (known !== undefined) return known
      // Область ключа — первое слово намерения: в журнале сервера по нему видно,
      // что именно повторял человек, без заглядывания в тело запроса.
      const scope = intent.split(":")[0] ?? "cabinet"
      const created = makeKey(scope)
      keys.set(intent, created)
      return created
    },
    forget(intent: string): void {
      keys.delete(intent)
    },
    size(): number {
      return keys.size
    },
  }
}

/** Действия панели записи. Строка попадает в область ключа и видна в журнале сервера. */
export type AppointmentAction =
  | "confirm"
  | "arrived"
  | "completed"
  | "no-show"
  | "no-show-undo"
  | "cancel"
  | "reschedule"
  | "update"

/**
 * Намерение «сделать с этой записью вот это, исходя из той версии, которую я вижу».
 *
 * Версия входит в намерение не для красоты: если запись за это время изменили, действие
 * всё равно не пройдёт (сервер ответит version_conflict), и человек примет решение
 * заново — а это уже другое намерение и другой ключ.
 */
export function appointmentIntent(
  action: AppointmentAction,
  appointmentId: string,
  expectedUpdatedAt: string,
): string {
  return `${action}:${appointmentId}:${expectedUpdatedAt}`
}
